import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import { Plus, Trash2, Edit, Menu } from 'lucide-react';
import { Button, Table, Input, message, Card, Modal } from 'antd';
import SidebarRole from './Modals/SidebarApp';
import { ApplicationRolesService } from '../../../api/index';
import Header from '../../../pages/Dashboard/Header';
import { ImportRolesCsv } from './ImportRolesCsv';
import { useTranslation } from '../../../../TranslationContext';

interface Role {
  id: string;
  name: string;
}

export const RolesPage = () => {
  const { t } = useTranslation();
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('company') || '';

  if (!appId || !companyId) {
    navigate('/error');
    return null;
  }

  const [, setMobileSidebarOpen] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const { data: roles = [], isLoading, error } = useSWR<Role[]>(
    ['roles', companyId, appId],
    () => ApplicationRolesService.getAll(companyId, appId)
      .then(res => {
        if (Array.isArray(res)) return res;
        if (res?.data && Array.isArray(res.data)) return res.data;
        console.warn(t('unexpected_response_format'), res);
        return [];
      })
      .catch(err => {
        setApiError(t('error_loading_roles'));
        console.error(err);
        return [];
      })
  );

  const handleAddRole = async () => {
    if (!newRoleName.trim()) return;

    try {
      const response = await ApplicationRolesService.create(
        companyId, 
        appId, 
        { name: newRoleName }
      );
      
      message.success(response.message || t('role_added_success'));
      setNewRoleName('');
      setAddModalVisible(false);
      mutate(['roles', companyId, appId]);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message 
        || error.message 
        || t('error_adding_role');
      message.error(errorMessage);
      setApiError(errorMessage);
    }
  };

  const handleEditRole = async () => {
    if (!editingRole?.id) return;

    try {
      const response = await ApplicationRolesService.update(
        companyId, 
        appId, 
        editingRole.id, 
        { name: editingRole.name }
      );
      
      message.success(response.message || t('role_modified_success'));
      setEditModalVisible(false);
      mutate(['roles', companyId, appId]);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message 
        || error.message 
        || t('error_modifying_role');
      message.error(errorMessage);
      setApiError(errorMessage);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    try {
      const response = await ApplicationRolesService.delete(
        companyId, 
        appId, 
        roleId
      );
      
      message.success(response.message || t('role_deleted_success'));
      mutate(['roles', companyId, appId]);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message 
        || error.message 
        || t('error_deleting_role');
      message.error(errorMessage);
      setApiError(errorMessage);
    }
  };

  const columns = [
    {
      title: t('role_name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_: any, record: Role) => (
        <div className="flex space-x-2">
          <Button 
            icon={<Edit size={16} />}
            onClick={() => {
              setEditingRole(record);
              setEditModalVisible(true);
            }}
            aria-label={t('edit_role')}
          />
          <Button 
            danger
            icon={<Trash2 size={16} />}
            onClick={() => handleDeleteRole(record.name)}
            aria-label={t('delete_role')}
          />
        </div>
      ),
    },
  ];

  const refreshRoles = () => {
    mutate(['roles', companyId, appId]);
  };

  return (
    <div> 
      <Header/>
      <div className="flex h-screen bg-gray-50">
        <div className="hidden md:block">
          <SidebarRole />
        </div>

        <div className="flex-1 overflow-auto">
          <div className="bg-white border-b border-gray-200 p-4 flex items-center md:hidden">
            <Button 
              icon={<Menu size={20} />}
              onClick={() => setMobileSidebarOpen(true)}
              aria-label={t('open_menu')}
            />
            <h1 className="text-xl font-semibold ml-4">{t('roles_management')}</h1>
          </div>

          <div className="p-6">
            <Card
              title={t('roles_management')}
              extra={
                <div className="flex space-x-2">
                  <ImportRolesCsv 
                    companyId={companyId} 
                    appId={appId} 
                    onSuccess={refreshRoles} 
                  />
                  <Button 
                    type="primary" 
                    icon={<Plus size={16} />}
                    onClick={() => {
                      setNewRoleName('');
                      setAddModalVisible(true);
                    }}
                  >
                    {t('add_role')}
                  </Button>
                </div>
              }
            >
              {apiError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600">{apiError}</p>
                </div>
              )}

              <Table 
                columns={columns}
                dataSource={roles}
                rowKey={(record) => record.id}
                loading={isLoading}
                locale={{ 
                  emptyText: error 
                    ? t('error_loading_roles') 
                    : t('no_roles_available')
                }}
              />
            </Card>

            <Modal
              title={t('add_new_role')}
              open={addModalVisible}
              onCancel={() => setAddModalVisible(false)}
              onOk={handleAddRole}
              okButtonProps={{ disabled: !newRoleName.trim() }}
              okText={t('add')}
              cancelText={t('cancel')}
            >
              <Input
                placeholder={t('enter_role_name')}
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                onPressEnter={handleAddRole}
              />
            </Modal>

            <Modal
              title={t('edit_role')}
              open={editModalVisible}
              onCancel={() => setEditModalVisible(false)}
              onOk={handleEditRole}
              okButtonProps={{ disabled: !editingRole?.name?.trim() }}
              okText={t('save')}
              cancelText={t('cancel')}
            >
              {editingRole && (
                <Input
                  placeholder={t('edit_role_name')}
                  value={editingRole.name}
                  onChange={(e) => setEditingRole({
                    ...editingRole,
                    name: e.target.value
                  })}
                  onPressEnter={handleEditRole}
                />
              )}
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPage;

// import { useState } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import useSWR, { mutate } from 'swr';
// import { Plus, Trash2, Edit, Menu } from 'lucide-react';
// import { Button, Table, Input, message, Card, Modal } from 'antd';
// import SidebarRole from './Modals/SidebarApp';
// import { ApplicationRolesService } from '../../../api/index';
// import Header from '../../../pages/Dashboard/Header';
// import { ImportRolesCsv } from './ImportRolesCsv';

// interface Role {
//   id: string;
//   name: string;
// }

// export const RolesPage = () => {
//   const { appId } = useParams<{ appId: string }>();
//   const navigate = useNavigate();
//   const companyId = localStorage.getItem('company') || '';

//   if (!appId || !companyId) {
//     navigate('/error');
//     return null;
//   }

//   const [, setMobileSidebarOpen] = useState(false);
//   const [addModalVisible, setAddModalVisible] = useState(false);
//   const [editModalVisible, setEditModalVisible] = useState(false);
//   const [newRoleName, setNewRoleName] = useState('');
//   const [editingRole, setEditingRole] = useState<Role | null>(null);
//   const [apiError, setApiError] = useState<string | null>(null);

//   const { data: roles = [], isLoading, error } = useSWR<Role[]>(
//     ['roles', companyId, appId],
//     () => ApplicationRolesService.getAll(companyId, appId)
//       .then(res => {
//         if (Array.isArray(res)) return res;
//         if (res?.data && Array.isArray(res.data)) return res.data;
//         console.warn('Format de réponse inattendu:', res);
//         return [];
//       })
//       .catch(err => {
//         setApiError('Erreur de chargement des rôles');
//         console.error(err);
//         return [];
//       })
//   );

//   const handleAddRole = async () => {
//     if (!newRoleName.trim()) return;

//     try {
//       const response = await ApplicationRolesService.create(
//         companyId, 
//         appId, 
//         { name: newRoleName }
//       );
      
//       message.success(response.message || ' Role successfully added');
//       setNewRoleName('');
//       setAddModalVisible(false);
//       mutate(['roles', companyId, appId]);
//     } catch (error: any) {
//       const errorMessage = error.response?.data?.message 
//         || error.message 
//         || "Error adding role";
//       message.error(errorMessage);
//       setApiError(errorMessage);
//     }
//   };

//   const handleEditRole = async () => {
//     if (!editingRole?.id) return;

//     try {
//       const response = await ApplicationRolesService.update(
//         companyId, 
//         appId, 
//         editingRole.id, 
//         { name: editingRole.name }
//       );
      
//       message.success(response.message || 'Role successfully modified');
//       setEditModalVisible(false);
//       mutate(['roles', companyId, appId]);
//     } catch (error: any) {
//       const errorMessage = error.response?.data?.message 
//         || error.message 
//         || "Role modification error";
//       message.error(errorMessage);
//       setApiError(errorMessage);
//     }
//   };

//   const handleDeleteRole = async (roleId: string) => {
//     try {
//       const response = await ApplicationRolesService.delete(
//         companyId, 
//         appId, 
//         roleId
//       );
      
//       message.success(response.message || 'Role successfully deleted');
//       mutate(['roles', companyId, appId]);
//     } catch (error: any) {
//       const errorMessage = error.response?.data?.message 
//         || error.message 
//         || "Error when deleting role";
//       message.error(errorMessage);
//       setApiError(errorMessage);
//     }
//   };

//   const columns = [
//     {
//       title: 'Role Name',
//       dataIndex: 'name',
//       key: 'name',
//     },
//     {
//       title: 'Actions',
//       key: 'actions',
//       render: (_: any, record: Role) => (
//         <div className="flex space-x-2">
//           <Button 
//             icon={<Edit size={16} />}
//             onClick={() => {
//               setEditingRole(record);
//               setEditModalVisible(true);
//             }}
//           />
//           <Button 
//             danger
//             icon={<Trash2 size={16} />}
//             onClick={() => handleDeleteRole(record.name)}
//           />
//         </div>
//       ),
//     },
//   ];

//   const refreshRoles = () => {
//     mutate(['roles', companyId, appId]);
//   };

//   return (
//     <div> 
//       <Header/>
//       <div className="flex h-screen bg-gray-50">
//         <div className="hidden md:block">
//           <SidebarRole />
//         </div>

//         <div className="flex-1 overflow-auto">
//           <div className="bg-white border-b border-gray-200 p-4 flex items-center md:hidden">
//             <Button 
//               icon={<Menu size={20} />}
//               onClick={() => setMobileSidebarOpen(true)}
//             />
//             <h1 className="text-xl font-semibold ml-4">Roles management</h1>
//           </div>

//           <div className="p-6">
//             <Card
//               title="Roles management"
//               extra={
//                 <div className="flex space-x-2">
//                   <ImportRolesCsv 
//                     companyId={companyId} 
//                     appId={appId} 
//                     onSuccess={refreshRoles} 
//                   />
//                   <Button 
//                     type="primary" 
//                     icon={<Plus size={16} />}
//                     onClick={() => {
//                       setNewRoleName('');
//                       setAddModalVisible(true);
//                     }}
//                   >
//                     Add a Role
//                   </Button>
//                 </div>
//               }
//             >
//               {apiError && (
//                 <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
//                   <p className="text-red-600">{apiError}</p>
//                 </div>
//               )}

//               <Table 
//                 columns={columns}
//                 dataSource={roles}
//                 rowKey={(record) => record.id}
//                 loading={isLoading}
//                 locale={{ 
//                   emptyText: error 
//                     ? 'Erreur de chargement des rôles' 
//                     : 'Aucun rôle disponible'
//                 }}
//               />
//             </Card>

//             <Modal
//               title="Ajouter un nouveau rôle"
//               open={addModalVisible}
//               onCancel={() => setAddModalVisible(false)}
//               onOk={handleAddRole}
//               okButtonProps={{ disabled: !newRoleName.trim() }}
//               okText="Ajouter"
//               cancelText="Annuler"
//             >
//               <Input
//                 placeholder="Entrez le nom du rôle"
//                 value={newRoleName}
//                 onChange={(e) => setNewRoleName(e.target.value)}
//                 onPressEnter={handleAddRole}
//               />
//             </Modal>

//             <Modal
//               title="Modifier le rôle"
//               open={editModalVisible}
//               onCancel={() => setEditModalVisible(false)}
//               onOk={handleEditRole}
//               okButtonProps={{ disabled: !editingRole?.name?.trim() }}
//               okText="Enregistrer"
//               cancelText="Annuler"
//             >
//               {editingRole && (
//                 <Input
//                   placeholder="Modifier le nom du rôle"
//                   value={editingRole.name}
//                   onChange={(e) => setEditingRole({
//                     ...editingRole,
//                     name: e.target.value
//                   })}
//                   onPressEnter={handleEditRole}
//                 />
//               )}
//             </Modal>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };